import { UserGroup } from "src/types/users";

export const COACHING_MANAGE_PATH = "/coaching/manage";
export const COACHING_EXPLORE_PATH = "/coaching/explore";
export const COACHING_REPORTS_PATH = "/coaching/reports";
export const COACHING_STUDIO_PATH = "/coaching/studio";
export const COACHING_MY_COURSES_PATH = "/coaching/courses";
export const COACHING_MY_PODCASTS_PATH = "/coaching/podcasts";

export type MySpaceCourseStatus = "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";

export type LearnerNavigationState = {
  from?: string;
  rootFrom?: string;
  mySpaceStatus?: MySpaceCourseStatus;
};

export type ManageNavigationState = {
  from?: string;
  rootFrom?: string;
  returnTo?: string;
  learnerViewFrom?: string;
  selectedUserGroups?: UserGroup[];
  mySpaceStatus?: MySpaceCourseStatus;
};

export type ManageNavigationTarget =
  | string
  | { path: string; state: ManageNavigationState };

export type CoachingEntityType = "course" | "simulation" | "podcast";

export const isPublishedOverviewPath = (path: string): boolean =>
  path.includes("/simulations/published/") ||
  path.includes("/courses/published/") ||
  path.includes("/podcasts/published/");

const isDraftEditPath = (path: string): boolean =>
  path.includes("/draft/") || path.includes("/edit/");

export const isLearnerDetailPath = (path: string): boolean => {
  if (!path || path.startsWith(COACHING_MANAGE_PATH) || isPublishedOverviewPath(path)) {
    return false;
  }

  return (
    /^\/coaching\/explore\/(courses|simulations|podcasts)\/[^/]+$/.test(path) ||
    /^\/coaching\/(courses|simulations|podcasts)\/[^/]+$/.test(path)
  );
};

export const isCoachingOriginPath = (path: string): boolean => {
  if (!path || isPublishedOverviewPath(path) || isDraftEditPath(path)) {
    return false;
  }

  if (isLearnerDetailPath(path)) {
    return false;
  }

  return (
    path === COACHING_MANAGE_PATH ||
    path === COACHING_STUDIO_PATH ||
    path === COACHING_REPORTS_PATH ||
    path === COACHING_EXPLORE_PATH ||
    path === COACHING_MY_COURSES_PATH ||
    path === COACHING_MY_PODCASTS_PATH
  );
};

const isLearnerOriginPath = (path: string): boolean => {
  if (path.startsWith(COACHING_MANAGE_PATH)) {
    return false;
  }

  if (isPublishedOverviewPath(path)) {
    return false;
  }

  return (
    path.startsWith(COACHING_EXPLORE_PATH) ||
    path.startsWith(COACHING_MY_COURSES_PATH) ||
    path.startsWith("/coaching/simulations/") ||
    path.startsWith(COACHING_MY_PODCASTS_PATH)
  );
};

const isPublishedCatalogStatus = (status?: string): boolean =>
  !status || status.toUpperCase() === "PUBLISHED";

export { isPublishedCatalogStatus };

export const getPublishedOverviewPath = (
  entityType: CoachingEntityType,
  entityId: string,
): string => {
  switch (entityType) {
    case "simulation":
      return `/coaching/manage/simulations/published/${entityId}`;
    case "podcast":
      return `/coaching/manage/podcasts/published/${entityId}`;
    default:
      return `/coaching/manage/courses/published/${entityId}`;
  }
};

export const getDraftEditPath = (
  entityType: CoachingEntityType,
  entityId: string,
): string => {
  switch (entityType) {
    case "simulation":
      return `/coaching/manage/simulations/draft/${entityId}`;
    case "podcast":
      return `/coaching/manage/podcasts/draft/${entityId}`;
    default:
      return `/coaching/manage/courses/draft/${entityId}`;
  }
};

export const getManageNavigationState = (state: unknown): ManageNavigationState => {
  if (!state || typeof state !== "object") {
    return {};
  }

  const navigationState = state as ManageNavigationState;

  return {
    from: typeof navigationState.from === "string" ? navigationState.from : undefined,
    rootFrom:
      typeof navigationState.rootFrom === "string"
        ? navigationState.rootFrom
        : undefined,
    returnTo:
      typeof navigationState.returnTo === "string"
        ? navigationState.returnTo
        : undefined,
    learnerViewFrom:
      typeof navigationState.learnerViewFrom === "string"
        ? navigationState.learnerViewFrom
        : undefined,
    selectedUserGroups: getManageNavigationSelectedUserGroups(state),
    mySpaceStatus: getLearnerNavigationState(state).mySpaceStatus,
  };
};

export const getLearnerViewFromPath = (state: unknown): string | undefined => {
  const navigationState = getManageNavigationState(state);

  if (
    navigationState.learnerViewFrom &&
    isLearnerDetailPath(navigationState.learnerViewFrom)
  ) {
    return navigationState.learnerViewFrom;
  }

  return [navigationState.returnTo, navigationState.from].find(
    (path) => path && isLearnerDetailPath(path),
  );
};

export const getManageNavigationRootFrom = (state: unknown): string | undefined => {
  const navigationState = getManageNavigationState(state);
  const { rootFrom, from } = navigationState;

  if (from && isCoachingOriginPath(from)) {
    return from;
  }

  if (from?.startsWith(COACHING_EXPLORE_PATH)) {
    return COACHING_EXPLORE_PATH;
  }

  if (rootFrom && isCoachingOriginPath(rootFrom)) {
    return rootFrom;
  }

  if (rootFrom?.startsWith(COACHING_EXPLORE_PATH)) {
    return COACHING_EXPLORE_PATH;
  }

  if (!from || isPublishedOverviewPath(from) || isDraftEditPath(from)) {
    if (
      rootFrom &&
      (rootFrom.includes("/reports") || rootFrom.includes("/studio"))
    ) {
      return rootFrom;
    }

    return isLearnerDetailPath(rootFrom ?? "") ? undefined : rootFrom;
  }

  if (from.includes("/reports") || from.includes("/studio")) {
    return from;
  }

  return undefined;
};

export const getManageNavigationFrom = (state: unknown): string | undefined => {
  const navigationState = getManageNavigationState(state);

  if (navigationState.from) {
    return navigationState.from;
  }

  return navigationState.rootFrom;
};

export const getManageNavigationSelectedUserGroups = (
  state: unknown,
): UserGroup[] | undefined => {
  if (!state || typeof state !== "object") {
    return undefined;
  }

  const { selectedUserGroups } = state as ManageNavigationState;

  if (!Array.isArray(selectedUserGroups) || selectedUserGroups.length === 0) {
    return undefined;
  }

  return selectedUserGroups.filter(
    (group): group is UserGroup =>
      !!group &&
      typeof group === "object" &&
      typeof (group as UserGroup).id === "string",
  );
};

export const buildManageNavigationState = (
  from?: string,
  rootFrom?: string,
  selectedUserGroups?: UserGroup[],
  mySpaceStatus?: MySpaceCourseStatus,
): ManageNavigationState | undefined => {
  const resolvedRootFrom = rootFrom ?? from;
  const groups =
    selectedUserGroups && selectedUserGroups.length > 0
      ? selectedUserGroups
      : undefined;

  if (!resolvedRootFrom && !groups && !mySpaceStatus) {
    return undefined;
  }

  const state: ManageNavigationState = {};

  if (resolvedRootFrom) {
    state.from = from ?? resolvedRootFrom;
    state.rootFrom = resolvedRootFrom;

    if (
      from &&
      isLearnerDetailPath(from) &&
      resolvedRootFrom !== from
    ) {
      state.returnTo = from;
    }
  }

  if (groups) {
    state.selectedUserGroups = groups;
  }

  if (mySpaceStatus) {
    state.mySpaceStatus = mySpaceStatus;
  }

  return state;
};

export const buildOriginNavigationState = (
  origin: string,
  selectedUserGroups?: UserGroup[],
): ManageNavigationState | undefined =>
  buildManageNavigationState(origin, origin, selectedUserGroups);

export const buildEditEntryState = (
  returnToPath: string,
  previousState: unknown,
): ManageNavigationState => {
  const previousNavigation = getManageNavigationState(previousState);
  const listOrigin =
    getManageNavigationRootFrom(previousState) ??
    (() => {
      const from = getManageNavigationFrom(previousState);
      return from && isCoachingOriginPath(from) ? from : undefined;
    })();

  const viewFrom = [previousNavigation.returnTo, previousNavigation.from].find(
    (path) => path && path !== returnToPath && isLearnerDetailPath(path),
  );

  const { mySpaceStatus } = getLearnerNavigationState(previousState);

  return {
    from: returnToPath,
    returnTo: viewFrom ?? returnToPath,
    ...(listOrigin ? { rootFrom: listOrigin } : {}),
    ...(mySpaceStatus ? { mySpaceStatus } : {}),
  };
};

export const resolveEditExitRootFrom = (options: {
  backPath?: string;
  rootFrom?: string;
  returnTo?: string;
  fallback?: string;
}): string => {
  if (options.rootFrom) {
    return options.rootFrom;
  }

  const candidates = [options.backPath, options.returnTo];

  for (const path of candidates) {
    if (path && isCoachingOriginPath(path)) {
      return path;
    }

    if (path && (path.includes("/reports") || path.includes("/studio"))) {
      return path;
    }
  }

  return options.fallback ?? COACHING_MANAGE_PATH;
};

export const navigateToPublishedOverview = (
  navigate: (path: string, options?: { state?: ManageNavigationState }) => void,
  entityType: CoachingEntityType,
  entityId: string,
  origin: string,
  selectedUserGroups?: UserGroup[],
  mySpaceStatus?: MySpaceCourseStatus,
  viewFrom?: string,
) => {
  const listOrigin = isCoachingOriginPath(origin)
    ? origin
    : origin.startsWith(COACHING_EXPLORE_PATH)
      ? COACHING_EXPLORE_PATH
      : origin;
  const navigationState = buildManageNavigationState(
    viewFrom ?? listOrigin,
    listOrigin,
    selectedUserGroups,
    mySpaceStatus,
  );
  const path = getPublishedOverviewPath(entityType, entityId);

  if (navigationState && viewFrom) {
    navigationState.learnerViewFrom = viewFrom;
  }

  if (navigationState) {
    navigate(path, { state: navigationState });
    return;
  }

  navigate(path);
};

export const navigateOverviewBack = (
  navigate: (path: string, options?: { state?: ManageNavigationState }) => void,
  state: unknown,
  fallback: string,
) => {
  const listOrigin = getManageNavigationRootFrom(state);
  const viewFrom = getLearnerViewFromPath(state);
  const { mySpaceStatus } = getLearnerNavigationState(state);

  if (viewFrom) {
    const learnerState = buildLearnerNavigationState({
      from:
        listOrigin && isCoachingOriginPath(listOrigin) ? listOrigin : undefined,
      mySpaceStatus,
    });
    navigate(viewFrom, learnerState ? { state: learnerState } : undefined);
    return;
  }

  const path = resolveOverviewBackPath(listOrigin) ?? fallback;
  const backState = buildMySpaceReturnState(path, mySpaceStatus);

  if (backState) {
    navigate(path, { state: backState });
    return;
  }

  navigate(path);
};

export const getMySpaceTabIndex = (
  state: unknown,
  categories: ReadonlyArray<{ status: MySpaceCourseStatus }>,
): number => {
  const { mySpaceStatus } = getLearnerNavigationState(state);

  if (!mySpaceStatus) {
    return 0;
  }

  const tabIndex = categories.findIndex(
    (category) => category.status === mySpaceStatus,
  );

  return tabIndex >= 0 ? tabIndex : 0;
};

export const getLearnerNavigationState = (
  state: unknown,
): LearnerNavigationState => {
  if (!state || typeof state !== "object") {
    return {};
  }

  const { from, rootFrom, mySpaceStatus } = state as LearnerNavigationState;

  return {
    from: typeof from === "string" ? from : undefined,
    rootFrom: typeof rootFrom === "string" ? rootFrom : undefined,
    mySpaceStatus:
      mySpaceStatus === "NOT_STARTED" ||
      mySpaceStatus === "IN_PROGRESS" ||
      mySpaceStatus === "COMPLETED"
        ? mySpaceStatus
        : undefined,
  };
};

export const buildLearnerNavigationState = (
  navigationState: LearnerNavigationState,
): LearnerNavigationState | undefined => {
  const next: LearnerNavigationState = {};

  if (navigationState.from) {
    next.from = navigationState.from;
  }

  if (navigationState.rootFrom) {
    next.rootFrom = navigationState.rootFrom;
  }

  if (navigationState.mySpaceStatus) {
    next.mySpaceStatus = navigationState.mySpaceStatus;
  }

  if (Object.keys(next).length === 0) {
    return undefined;
  }

  return next;
};

export const navigateLearnerBack = (
  navigate: (
    path: string,
    options?: { state?: LearnerNavigationState },
  ) => void,
  navigationState: LearnerNavigationState | undefined,
  defaultPath: string = COACHING_MY_COURSES_PATH,
) => {
  const from = navigationState?.from;
  const listOrigin = navigationState?.rootFrom ?? defaultPath;

  if (from && isLearnerDetailPath(from)) {
    navigate(from, {
      state: buildLearnerNavigationState({
        from: listOrigin,
        mySpaceStatus: navigationState?.mySpaceStatus,
      }),
    });
    return;
  }

  const path = resolveLearnerBackPath(from, defaultPath);
  const backState = buildMySpaceReturnState(path, navigationState?.mySpaceStatus);

  if (backState) {
    navigate(path, { state: backState });
    return;
  }

  navigate(path);
};

export const buildMySpaceReturnState = (
  path: string,
  mySpaceStatus: MySpaceCourseStatus | undefined,
): LearnerNavigationState | undefined => {
  if (path !== COACHING_MY_COURSES_PATH || !mySpaceStatus) {
    return undefined;
  }

  return { mySpaceStatus };
};

export const resolveLearnerBackPath = (
  from: string | undefined,
  defaultPath: string = COACHING_MY_COURSES_PATH,
): string => {
  const path = from ?? defaultPath;

  if (path === COACHING_MY_PODCASTS_PATH) {
    return COACHING_MY_COURSES_PATH;
  }

  return path;
};

export const resolveOverviewBackPath = (
  from: string | undefined,
): string | undefined => {
  if (!from) {
    return undefined;
  }

  if (from === COACHING_MY_PODCASTS_PATH) {
    return COACHING_MY_COURSES_PATH;
  }

  return from;
};

export const getOverviewBackLabel = (from: string | undefined): string => {
  const path = resolveOverviewBackPath(from);

  if (path === COACHING_EXPLORE_PATH || path?.startsWith(`${COACHING_EXPLORE_PATH}/`)) {
    return 'back';
  }
  if (path?.includes("/reports")) {
    return 'back';
  }
  if (path?.includes("/studio")) {
    return 'back to "studio"';
  }
  if (path === COACHING_MY_COURSES_PATH) {
    return 'back';
  }
  if (path?.includes("/manage")) {
    return 'back to "manage"';
  }

  return "back";
};

export const getManageReportsBackLabel = (
  entityType: "course" | "simulation",
): string => (entityType === "simulation" ? "simulations" : "courses");

export const navigateManageLearnerReportsBack = (
  navigate: (path: string, options?: { state?: ManageNavigationState }) => void,
  options: {
    from?: string;
    entityType: "course" | "simulation";
    entityId: string;
    fallback?: string;
    selectedUserGroups?: UserGroup[];
    navigationState?: unknown;
  },
) => {
  const { entityType, entityId, selectedUserGroups, navigationState } = options;
  const manageState = getManageNavigationState(navigationState);
  const listOrigin =
    getManageNavigationRootFrom(navigationState) ??
    options.from ??
    options.fallback ??
    COACHING_MANAGE_PATH;
  const { mySpaceStatus } = getLearnerNavigationState(navigationState);
  const draftReturnPath =
    manageState.returnTo && isDraftEditPath(manageState.returnTo)
      ? manageState.returnTo
      : undefined;

  if (draftReturnPath) {
    const publishedOverviewPath = isPublishedOverviewPath(manageState.from ?? "")
      ? manageState.from!
      : getPublishedOverviewPath(entityType, entityId);
    const learnerViewFrom = getLearnerViewFromPath(navigationState);

    const state = buildManageNavigationState(
      publishedOverviewPath,
      listOrigin,
      selectedUserGroups,
      mySpaceStatus,
    );

    if (state) {
      state.returnTo = learnerViewFrom ?? publishedOverviewPath;
    }

    navigate(draftReturnPath, state ? { state } : undefined);
    return;
  }

  navigateToPublishedOverview(
    navigate,
    entityType,
    entityId,
    listOrigin,
    selectedUserGroups,
    mySpaceStatus,
    getLearnerViewFromPath(navigationState),
  );
};

export const getLearnerBackLabel = (from: string | undefined): string => {
  const path = resolveOverviewBackPath(from) ?? COACHING_MY_COURSES_PATH;

  if (path.includes("/explore")) {
    return "explore";
  }
  if (path.includes("/reports")) {
    return "reports";
  }
  if (path.includes("/studio")) {
    return "studio";
  }
  if (path.includes("/manage")) {
    return "manage";
  }

  return "my courses";
};

export const getSimulationManageLink = (
  simulationId: string,
  options: { published?: boolean; status?: string; from?: string } = {},
): ManageNavigationTarget => {
  const from = options.from ?? COACHING_MANAGE_PATH;
  const isPublishedView =
    options.published || options.status === "PUBLISHED";

  if (isPublishedView) {
    return {
      path: `/coaching/manage/simulations/published/${simulationId}`,
      state: buildManageNavigationState(from, from) ?? { from, rootFrom: from },
    };
  }

  return getDraftEditPath("simulation", simulationId);
};

export const getCourseManageLink = (
  courseId: string,
  options: { published?: boolean; status?: string; from?: string } = {},
): ManageNavigationTarget => {
  const from = options.from ?? COACHING_MANAGE_PATH;
  const isPublishedView =
    options.published || options.status === "PUBLISHED";

  if (isPublishedView) {
    return {
      path: `/coaching/manage/courses/published/${courseId}`,
      state: buildManageNavigationState(from, from) ?? { from, rootFrom: from },
    };
  }

  return getDraftEditPath("course", courseId);
};

export const getPodcastManageLink = (
  podcastId: string,
  options: { published?: boolean; status?: string; from?: string } = {},
): ManageNavigationTarget => {
  const from = options.from ?? COACHING_MANAGE_PATH;
  const isPublishedView =
    options.published || options.status === "PUBLISHED";

  if (isPublishedView) {
    return {
      path: `/coaching/manage/podcasts/published/${podcastId}`,
      state: buildManageNavigationState(from, from) ?? { from, rootFrom: from },
    };
  }

  return getDraftEditPath("podcast", podcastId);
};

export const getContentManageLink = (
  entityType: string | undefined,
  contentId: string,
  options: {
    from?: string;
    status?: string;
    published?: boolean;
  } = {},
): ManageNavigationTarget | "" => {
  if (!contentId || !entityType) {
    return "";
  }

  if (entityType === "simulation") {
    return getSimulationManageLink(contentId, options);
  }

  if (entityType === "course") {
    return getCourseManageLink(contentId, options);
  }

  if (entityType === "podcast") {
    return getPodcastManageLink(contentId, options);
  }

  return "";
};

export const navigateToManageTarget = (
  navigate: (path: string, options?: { state?: ManageNavigationState }) => void,
  target: ManageNavigationTarget,
  options: { from?: string; rootFrom?: string; navigationState?: unknown } = {},
) => {
  const navigationListOrigin = getManageNavigationRootFrom(options.navigationState);
  const exploreOrigin =
    options.rootFrom === COACHING_EXPLORE_PATH ||
    options.from?.startsWith(COACHING_EXPLORE_PATH) ||
    navigationListOrigin === COACHING_EXPLORE_PATH
      ? COACHING_EXPLORE_PATH
      : undefined;
  const rootFrom =
    exploreOrigin ??
    options.rootFrom ??
    options.from ??
    COACHING_MANAGE_PATH;
  const { mySpaceStatus } = getLearnerNavigationState(options.navigationState);

  if (typeof target === "string") {
    const entryFrom = options.from ?? rootFrom;
    const navigationState = buildManageNavigationState(
      entryFrom,
      rootFrom,
      undefined,
      mySpaceStatus,
    );
    navigate(target, navigationState ? { state: navigationState } : undefined);
    return;
  }

  const targetRootFrom = target.state.rootFrom ?? target.state.from ?? rootFrom;
  const entryFrom = isLearnerDetailPath(options.from ?? "")
    ? options.from
    : (target.state.from ?? targetRootFrom);
  const entryRootFrom = isLearnerDetailPath(options.from ?? "")
    ? rootFrom
    : targetRootFrom;
  const navigationState = buildManageNavigationState(
    entryFrom,
    entryRootFrom,
    target.state.selectedUserGroups,
    mySpaceStatus ?? target.state.mySpaceStatus,
  );

  navigate(target.path, {
    state: navigationState ?? target.state,
  });
};

export const navigateCoachingEditExit = (
  navigate: (path: string, options?: { state?: ManageNavigationState }) => void,
  options: {
    backPath?: string;
    rootFrom?: string;
    returnTo?: string;
    fallback?: string;
    entityType?: CoachingEntityType;
    entityId?: string;
    navigationState?: unknown;
  } = {},
) => {
  const { backPath, returnTo, entityType, entityId, navigationState } = options;
  const resolvedRootFrom = resolveEditExitRootFrom(options);
  const { mySpaceStatus } = getLearnerNavigationState(navigationState);
  const immediateBack = returnTo ?? backPath;
  const publishedOverviewTarget = [returnTo, backPath].find(
    (path) => path && isPublishedOverviewPath(path),
  );

  const listOrigin = getManageNavigationRootFrom(navigationState) ?? resolvedRootFrom;
  const viewFrom = getLearnerViewFromPath(navigationState);
  const entryOrigin = listOrigin ?? resolvedRootFrom;

  if (entityType && entityId && publishedOverviewTarget) {
    navigateToPublishedOverview(
      navigate,
      entityType,
      entityId,
      listOrigin,
      undefined,
      mySpaceStatus,
      viewFrom,
    );
    return;
  }

  if (
    entityType &&
    entityId &&
    immediateBack &&
    (isLearnerDetailPath(immediateBack) ||
      (isLearnerOriginPath(immediateBack) && resolvedRootFrom))
  ) {
    navigateToPublishedOverview(
      navigate,
      entityType,
      entityId,
      listOrigin,
      undefined,
      mySpaceStatus,
      viewFrom,
    );
    return;
  }

  if (!immediateBack) {
    const backState = buildMySpaceReturnState(resolvedRootFrom, mySpaceStatus);
    navigate(resolvedRootFrom, backState ? { state: backState } : undefined);
    return;
  }

  if (isPublishedOverviewPath(immediateBack)) {
    const state = buildManageNavigationState(
      viewFrom ?? listOrigin,
      listOrigin,
      undefined,
      mySpaceStatus,
    );
    navigate(immediateBack, state ? { state } : undefined);
    return;
  }

  if (isCoachingOriginPath(immediateBack)) {
    const skipPublishedRedirect =
      (entryOrigin === COACHING_STUDIO_PATH &&
        immediateBack === COACHING_STUDIO_PATH) ||
      (entryOrigin === COACHING_MANAGE_PATH &&
        immediateBack === COACHING_MANAGE_PATH);

    if (entityType && entityId && !skipPublishedRedirect) {
      navigateToPublishedOverview(
        navigate,
        entityType,
        entityId,
        listOrigin,
        undefined,
        mySpaceStatus,
        viewFrom,
      );
      return;
    }

    const backState = buildMySpaceReturnState(immediateBack, mySpaceStatus);
    navigate(immediateBack, backState ? { state: backState } : undefined);
    return;
  }

  navigate(immediateBack);
};

export const isLearnerCoachingPath = (pathname: string): boolean =>
  pathname.startsWith(COACHING_EXPLORE_PATH) ||
  pathname.startsWith(COACHING_MY_COURSES_PATH) ||
  pathname.startsWith("/coaching/simulations/") ||
  pathname.startsWith("/coaching/courses/") ||
  pathname.startsWith(COACHING_MY_PODCASTS_PATH);
